import { useState } from 'react';

import {
  adminAllClients,
  adminCreateGroupChannel,
  adminGetAllClients,
  adminGetAllClientsPagination,
  bookedRequestedPharmacies,
  bookedRequestedPharmacists,
  chatActions,
  createChatFromPharmacy,
  createChatFromProfessional,
  getRequestOrBookedPharmacies,
  getRequestOrBookedPharmaciesPagination,
  getRequestOrBookedPharmacists,
  getRequestOrBookedPharmacistsPagination,
  getTypeOfUser,
  HelpTypes,
  TypeOfUser,
} from '@pharmaplan/common';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';

const { pharmacist, pharmacy, admin, superAdmin } = TypeOfUser;

const userTypes = [HelpTypes.pharmacist, HelpTypes.pharmacy, HelpTypes.technicians, HelpTypes.students];
const {
  adminCreateGroupChannel: adminCreateGroupAction,
  createGroupFromPharmacy,
  createGroupFromProfessional,
} = chatActions;

const useCreateNewChat = () => {
  const dispatch = useAppDispatch();

  const userType = useAppSelector(getTypeOfUser);

  const [page, setPage] = useState(1);

  const adminsUserList = useAppSelector(adminAllClients);
  const pharmaciesUserList = useAppSelector(bookedRequestedPharmacies);
  const pharmacistUserList = useAppSelector(bookedRequestedPharmacists);

  const { data: pharmacyData } = pharmaciesUserList ?? {};
  const { data: pharmacistData } = pharmacistUserList ?? {};
  const { data } = adminsUserList ?? {};

  const config = () => {
    switch (userType) {
      case admin:
      case superAdmin:
        return {
          label: '',
          successAction: adminCreateGroupAction,
          data,
          createGroup: (userId: string) =>
            dispatch(adminCreateGroupChannel(userId)),
          getUsersWithPage: (input: string) =>
            dispatch(
              adminGetAllClientsPagination({
                pagingModel: {
                  page,
                },
                active: true,
                userTypes,
                userText: input,
              }),
            ),
          getUsers: () =>
            dispatch(
              adminGetAllClients({
                pagingModel: { page: 1 },
                userTypes,
                active: true,
              }),
            ),
          getUsersWithName: (input: string) =>
            dispatch(
              adminGetAllClients({
                pagingModel: {
                  page: 1,
                },
                userTypes,
                userText: input.trim(),
                active: true,
              }),
            ),
        };
      case pharmacist:
        return {
          label: strings.pharmacies.toLowerCase(),
          successAction: createGroupFromProfessional,
          data: pharmacyData,
          createGroup: (userId: string) =>
            dispatch(createChatFromProfessional(userId)),
          getUsers: () =>
            dispatch(
              getRequestOrBookedPharmacies({ pagingModel: { page: 1 } }),
            ),
          getUsersWithPage: (input: string) =>
            getRequestOrBookedPharmaciesPagination({
              pagingModel: { page },
              userText: input.trim(),
            }),
          getUsersWithName: (input: string) =>
            dispatch(
              getRequestOrBookedPharmacies({
                pagingModel: { page: 1 },
                userText: input.trim(),
              }),
            ),
        };

      case pharmacy:
        return {
          label: strings.pharmacists.toLowerCase(),
          successAction: createGroupFromPharmacy,
          data: pharmacistData,
          createGroup: (userId: string) =>
            dispatch(createChatFromPharmacy(userId)),
          getUsers: () =>
            dispatch(
              getRequestOrBookedPharmacists({ pagingModel: { page: 1 } }),
            ),
          getUsersWithPage: (input: string) =>
            getRequestOrBookedPharmacistsPagination({
              pagingModel: { page },
              userText: input.trim(),
            }),
          getUsersWithName: (input: string) =>
            dispatch(
              getRequestOrBookedPharmacists({
                pagingModel: { page: 1 },
                userText: input.trim(),
              }),
            ),
        };

      default:
        return {
          successAction: createGroupFromPharmacy,
          data: [],
        };
    }
  };

  return { config: config(), page, setPage };
};

export default useCreateNewChat;
